import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Table, { TableFooter, TableHeading } from "@/components/Table";
import InteractionSideSheet from "@/features/global/components/InteractionSideSheet";
import NoteSideSheet from "@/features/global/components/NoteSideSheet/NoteSideSheet";
import { useCanViewInteractionsBool } from "@/hooks/useCapabilities";
import { getColumns } from "@/pages/people/[pgaId]/interactions/person-history/_components/TimelineTableColumns";
import { PERSON_TIMELINE_QUERY } from "@/pages/people/[pgaId]/interactions/person-history/queries";
import {
  getPersonTimelinePreviewDateRange,
  PERSON_TIMELINE_PREVIEW_MAX_ROWS,
  PERSON_TIMELINE_PREVIEW_MONTHS_FALLBACK,
  PERSON_TIMELINE_PREVIEW_MONTHS_PRIMARY,
  PERSON_TIMELINE_PREVIEW_TIMEZONE
} from "@/pages/people/[pgaId]/interactions/person-history/personTimelinePreview";
import {
  canOpenTimelineRow,
  PersonTimelineObjectType,
  type PersonTimelineRow
} from "@/pages/people/[pgaId]/interactions/person-history/types";
import { getTimelineObjectId } from "@/pages/people/[pgaId]/interactions/person-history/utils";
import { DateTime } from "luxon";
import type { Row } from "@tanstack/react-table";

const PREVIEW_ORDER_BY = {
  field: "OCCURRED_AT" as const,
  direction: "DESC" as const
};

interface OverviewPersonHistoryProps {
  pgaId: string;
  firstUnderTabs?: boolean;
}

export function OverviewPersonHistory({ pgaId, firstUnderTabs = false }: OverviewPersonHistoryProps) {
  const navigate = useNavigate();
  const canViewInteractions = useCanViewInteractionsBool();
  const [selectedRow, setSelectedRow] = useState<PersonTimelineRow | null>(null);

  useEffect(() => {
    setSelectedRow(null);
  }, [pgaId]);

  const previewDayKey = DateTime.now().setZone(PERSON_TIMELINE_PREVIEW_TIMEZONE).toISODate()!;
  const primaryDateRange = useMemo(
    () => getPersonTimelinePreviewDateRange(PERSON_TIMELINE_PREVIEW_MONTHS_PRIMARY, previewDayKey),
    [previewDayKey]
  );
  const fallbackDateRange = useMemo(
    () => getPersonTimelinePreviewDateRange(PERSON_TIMELINE_PREVIEW_MONTHS_FALLBACK, previewDayKey),
    [previewDayKey]
  );

  const {
    data: primaryData,
    loading: primaryLoading,
    error: primaryError
  } = useQuery(PERSON_TIMELINE_QUERY, {
    variables: {
      pgaId,
      first: PERSON_TIMELINE_PREVIEW_MAX_ROWS,
      input: { dateRange: primaryDateRange },
      orderBy: PREVIEW_ORDER_BY
    },
    skip: !canViewInteractions
  });

  const primaryRows = primaryData?.personTimeline?.nodes ?? [];
  const useFallbackRange = !primaryLoading && !primaryError && primaryRows.length === 0;

  const {
    data: fallbackData,
    loading: fallbackLoading,
    error: fallbackError
  } = useQuery(PERSON_TIMELINE_QUERY, {
    variables: {
      pgaId,
      first: PERSON_TIMELINE_PREVIEW_MAX_ROWS,
      input: { dateRange: fallbackDateRange },
      orderBy: PREVIEW_ORDER_BY
    },
    skip: !canViewInteractions || !useFallbackRange
  });

  const handleOpenRow = useCallback((row: PersonTimelineRow) => {
    if (!canOpenTimelineRow(row)) return;

    setSelectedRow(row);
  }, []);

  const handleCloseSideSheet = useCallback(() => {
    setSelectedRow(null);
  }, []);

  const handleViewSource = useCallback(() => {
    if (!selectedRow?.sourcePath) return;
    navigate(selectedRow.sourcePath);
  }, [navigate, selectedRow?.sourcePath]);

  const columns = useMemo(() => getColumns({ onViewRow: handleOpenRow, sortable: false }), [handleOpenRow]);

  if (!canViewInteractions) {
    return null;
  }

  const timelineRows = useFallbackRange ? (fallbackData?.personTimeline?.nodes ?? []) : primaryRows;
  const rows = timelineRows.slice(0, PERSON_TIMELINE_PREVIEW_MAX_ROWS);
  const loading = primaryLoading || (useFallbackRange && fallbackLoading);
  const error = primaryError ?? fallbackError;
  const selectedObjectId = selectedRow ? getTimelineObjectId(selectedRow) : null;
  const isInteractionSideSheetOpen = selectedRow?.objectType === PersonTimelineObjectType.INTERACTION;
  const isNoteSideSheetOpen = selectedRow?.objectType === PersonTimelineObjectType.NOTE;

  if (error) {
    return (
      <ErrorBoundary variant="inline" title="Unable to Load Person History" message={error.message} className="mt-12" />
    );
  }

  return (
    <>
      {firstUnderTabs && <div className="pt-12" />}
      <TableHeading
        actions={[
          {
            label: "View Full History",
            variant: "outlined",
            onClick: () => navigate(`/people/${pgaId}/interactions/person-history`),
            testId: "view-full-person-history"
          }
        ]}
        topSpacing={firstUnderTabs ? "none" : "compact"}
      />
      <Table
        data={rows}
        columns={columns}
        loading={loading}
        onClickRow={(row: Row<PersonTimelineRow>) => handleOpenRow(row.original)}
        getRowClasses={(row) => (canOpenTimelineRow(row.original) ? "" : "cursor-default")}
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="timeline"
            title="No Recent Person History"
            caption="Recent engagements and milestones will appear here as history becomes available."
          />
        )}
        renderFooter={loading ? undefined : () => <TableFooter totalRowCount={rows.length} />}
      />
      <NoteSideSheet
        open={isNoteSideSheetOpen}
        onClose={handleCloseSideSheet}
        noteId={isNoteSideSheetOpen ? selectedObjectId : null}
        customActionButtons={
          selectedRow?.sourcePath
            ? [
                {
                  text: "View Note",
                  variant: "outlined",
                  onClick: handleViewSource,
                  testId: "view-person-history-source"
                }
              ]
            : undefined
        }
      />
      <InteractionSideSheet
        open={isInteractionSideSheetOpen}
        onClose={handleCloseSideSheet}
        interactionId={isInteractionSideSheetOpen ? selectedObjectId : null}
        pgaId={pgaId}
        customActionButtons={
          selectedRow?.sourcePath
            ? [
                {
                  text: "View Interaction",
                  variant: "outlined",
                  onClick: handleViewSource,
                  testId: "view-person-history-source"
                }
              ]
            : undefined
        }
      />
    </>
  );
}
